import { GenericDropdownOption } from 'App.types';
import { influencer, influencerQueries } from '@round/api';
import useSelectProps from 'Hooks/useSelectProps';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SelectProps } from '@round/ui-kit';
import { getCreatorName } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';

const queryOptions = influencerQueries.creatorExplore.options.infinite;

export type CreatorSelectOption = GenericDropdownOption<string> & influencer.CreatorSearchResult;
export const mapCreatorToOption = (creator: influencer.CreatorSearchResult): CreatorSelectOption => ({
    value: creator._id,
    label: getCreatorName(creator),
    ...creator,
});

type PropGetter = (
    override?: Pick<SelectProps<CreatorSelectOption>, 'formatOptionLabel' | 'onChange' | 'onInputChange' | 'inputValue'>
) => SelectProps<CreatorSelectOption>;

type Params = {
    initialValue?: CreatorSelectOption | null;
    params?: Pick<influencer.GetCreatorExploreParams, 'platforms' | 'client_id'>;
    enabled?: boolean;
};

type ReturnType = {
    getProps: PropGetter;
};

export default function useCreatorSelect({ initialValue, params, enabled = true }: Params = {}): ReturnType {
    const { debouncedSearchValue, isMenuOpen, getProps } = useSelectProps<CreatorSelectOption, false>({ initialValue });
    const query = useInfiniteQuery({
        ...queryOptions({
            ...params,
            search: debouncedSearchValue,
        }),
        enabled: isMenuOpen && enabled,
    });

    const options = query.data?.pages.flatMap((page) => page.results.map(mapCreatorToOption)) ?? [];

    return {
        getProps: (override) =>
            getProps({
                ...override,
                options,
                onMenuScrollToBottom: () => {
                    if (!query.isFetching && query.hasNextPage) {
                        query.fetchNextPage();
                    }
                },
                filterOption: null,
                isLoading: query.isFetching,
                noOptionsMessage: () => (query.isError ? 'Could not get creators' : 'No options'),
            }),
    };
}
